
import React from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Button,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Tooltip,
  Container,
  useTheme,
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import { useAuthenticatedUser } from '../../../hooks/useAuthenticatedUser';
import { Collection } from '../../../enums/collections.enum';
import { LeaderboardRounded } from '@mui/icons-material';
import { getRoleLabel } from '../../../utils/userRoleLabel';

type AppBarFormProps = {
  anchorElUser: HTMLElement | null;
  language: string;
  onOpenUserMenu: (event: React.MouseEvent<HTMLElement>) => void;
  onCloseUserMenu: () => void;
  onLanguageChange: (event: SelectChangeEvent) => void;
  onLogout: () => void;
};

export const AppBarForm: React.FC<AppBarFormProps> = ({
  anchorElUser,
  language,
  onOpenUserMenu,
  onCloseUserMenu,
  onLogout,
}) => {
  const { user, isAdmin } = useAuthenticatedUser();
  const theme = useTheme();
  const location = useLocation();
  const userDisplayName = [user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.email;


  const gradientBg = `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`;

  const settings = [
    { label: 'All Users', to: '/admin-users', disabled: !isAdmin },
    { label: 'Change Password', to: '/change-password' },
    { label: `Logout`, to: '' },
  ];
 
  return (
    <AppBar
      // position="fixed"
      // elevation={0} // <-- removes default shadow
      sx={{
        backgroundColor: gradientBg,
        boxShadow: 'none', // <-- ensures no shadow
        borderBottom: 'none', // <-- removes any bottom border
        marginBottom: '100px'
      }}

    >
      <Container maxWidth="xl">
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          {/* Logo */}
          <Box
            component={Link}
            to="/"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              textDecoration: 'none',
              px: 1,
              py: 0.5,
              borderRadius: 2,
              transition: 'background-color 0.2s',
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.08)' },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'rgba(255,255,255,0.18)',
                borderRadius: 1.5,
                p: 0.75,
              }}
            >
              <LeaderboardRounded sx={{ color: '#fff', fontSize: 22 }} />
            </Box>
            <Box>
              <Typography
                sx={{
                  fontWeight: 800,
                  fontSize: '1.05rem',
                  letterSpacing: '0.08em',
                  color: '#fff',
                  lineHeight: 1.1,
                  textTransform: 'uppercase',
                }}
              >
                RankMe
              </Typography>
              <Typography
                sx={{
                  fontSize: '0.58rem',
                  letterSpacing: '0.15em',
                  color: 'rgba(255,255,255,0.65)',
                  lineHeight: 1,
                  textTransform: 'uppercase',
                }}
              >
                Staff Reviews
              </Typography>
            </Box>
          </Box>

          {/* Right Section */}
          <Box display="flex" alignItems="center" gap={2}>
            {/* Dashboard Button */}
            {user && (
              <Button
                component={Link}
                to={`/${Collection.Companies}/${isAdmin ? '' : user?.companyId}`}
                variant="outlined"
                sx={{
                  color: '#fff',
                  borderColor: '#fff',
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 500,
                  '&:hover': {
                    backgroundColor: '#ffffff20',
                  },
                }}
              >
                Dashboard
              </Button>
            )}


            {user ? (
              <>
                <Tooltip title="Account settings">
                  <IconButton onClick={onOpenUserMenu} sx={{ p: 0 }}>
                    <Avatar
                      src={user.image || '/assets/default-user.jpg'}
                      alt={user.firstName}
                      sx={{
                        width: 40,
                        height: 40,
                        border: '2px solid #fff',
                      }}
                    />
                  </IconButton>
                </Tooltip>
                <Menu
                  anchorEl={anchorElUser}
                  open={Boolean(anchorElUser)}
                  onClose={onCloseUserMenu}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  sx={{ mt: 1 }}
                  PaperProps={{
                    elevation: 4,
                    sx: {
                      borderRadius: 2,
                      mt: 1,
                      minWidth: 200,
                    },
                  }}
                >
                  <MenuItem
                    key={'userData'}
                    sx={{
                      opacity: 1,
                      display: 'block',
                      py: 1,
                      px: 1.5,
                      mx: 1,
                      mt: 1,
                      mb: 0.5,
                      borderRadius: 1,
                      borderLeft: 4,
                      borderColor: 'primary.main',
                      backgroundColor: 'action.hover',
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 800, lineHeight: 1.2, color: 'primary.dark' }}>
                      {userDisplayName}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.primary', fontWeight: 600 }}>
                      {user?.email}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 700, display: 'block' }}>
                      Role: {getRoleLabel(user?.role)}
                    </Typography>
                  </MenuItem>
               
                  {settings
                    .filter(({ disabled }) => !disabled)
                    .map(({ label, to }) => (
                      <MenuItem
                        key={label}
                        component={label !== 'Logout' ? Link : 'button'}
                        to={label !== 'Logout' ? to : undefined}
                        onClick={() => {
                          onCloseUserMenu();
                          if (label === 'Logout') onLogout();
                        }}
                        sx={{ fontWeight: 500 }}
                      >
                        {label}
                      </MenuItem>
                    ))}
                </Menu>
              </>
            ) : location.pathname === '/' ? (<Button
              component={Link}
              to="/login"
              variant="contained"
              sx={{
                borderRadius: 2,
                fontWeight: 600,
                backgroundColor: '#1976d2',
                color: '#ffffff',
                '&:hover': {
                  backgroundColor: '#f0f0f0',
                },
              }}
            >
              Login
            </Button>) : <></>}

          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
};
